import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  AddRounded as AddIcon,
  DeleteRounded as DeleteIcon
} from '@material-ui/icons';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Capitalized } from 'components/styled';
import { Gap } from 'components/spacer';
import { Notification } from '../notification';
import { LineItem } from '../../domain-types';

const Line = styled(Box)`
  margin-bottom: 1px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const LineItemRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing(1)}px;
  align-items: flex-end;
  margin-bottom: ${p => p.theme.spacing(1)}px;
`;

const ReceiptRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(1)}px;
  margin-bottom: ${p => p.theme.spacing(0.5)}px;
`;

export interface CreateExpenseClaimResult {
  iban: string;
  lineItems: LineItem[];
  receiptFiles: Array<{ name: string; data: string; contentType: string }>;
  existingReceiptFileKeys: string[];
}

interface DraftLineItem extends LineItem {
  rowId: number;
  lineItemType: 'expense' | 'travel';
}

interface ExistingDraftReceiptFile {
  rowId: number;
  key: string;
  name: string;
}

interface DraftReceiptUploadFile {
  rowId: number;
  name: string;
  data: string;
  contentType: string;
}

type DraftReceiptFile = ExistingDraftReceiptFile | DraftReceiptUploadFile;

interface InitialReceiptFile {
  key: string;
  name: string;
}

interface Props {
  dialogOpen: boolean;
  initialIban?: string;
  initialLineItems?: LineItem[];
  initialReceiptFiles?: InitialReceiptFile[];
  defaultKmRate?: number;
  title?: string;
  onClose: () => void;
  onSubmit: (result: CreateExpenseClaimResult) => Promise<void>;
  onSaveDraft?: (result: CreateExpenseClaimResult) => Promise<void>;
}

export const MAX_RECEIPT_TOTAL_BYTES = 10 * 1024 * 1024;
const MAX_RECEIPT_IMAGE_DIMENSION = 2000;
const MIN_RECEIPT_IMAGE_DIMENSION = 1;
const RECEIPT_JPEG_QUALITY = 0.8;

const emptyLineItem = (): LineItem => ({
  date: new Date().toISOString().substring(0, 10),
  description: '',
  amountIncl: 0
});

const emptyTravelItem = (kmRate: number): LineItem => ({
  ...emptyLineItem(),
  kmTraveled: 0,
  kmRate
});

const estimateDecodedSizeBytes = (base64: string) => {
  const padding = (base64.match(/=*$/) || [''])[0].length;
  return Math.floor((base64.length * 3) / 4) - padding;
};

const getBase64Data = (dataUrl: string) => {
  const [, data = ''] = dataUrl.split(',');
  if (!data) {
    throw new Error('Unable to read file data');
  }
  return data;
};

const renameAsJpeg = (name: string) => {
  const strippedName = name.includes('.') ? name.replace(/\.[^.]+$/, '') : name;
  return `${strippedName.trim() || 'receipt'}.jpg`;
};

const readFileAsDataUrl = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl !== 'string') {
        reject(new Error('Unable to read file'));
        return;
      }
      resolve(dataUrl);
    };
    reader.onerror = () => reject(new Error('Unable to read file'));
    reader.readAsDataURL(file);
  });

const loadImageFile = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to process image'));
    };
    image.src = objectUrl;
  });

const prepareReceiptFile = async (file: File) => {
  if (!file.type.startsWith('image/')) {
    const dataUrl = await readFileAsDataUrl(file);
    return {
      name: file.name,
      data: getBase64Data(dataUrl),
      contentType: file.type || 'application/octet-stream'
    };
  }

  const image = await loadImageFile(file);
  const scale = Math.min(
    1,
    MAX_RECEIPT_IMAGE_DIMENSION / Math.max(image.width, image.height)
  );
  const width = Math.max(
    MIN_RECEIPT_IMAGE_DIMENSION,
    Math.round(image.width * scale)
  );
  const height = Math.max(
    MIN_RECEIPT_IMAGE_DIMENSION,
    Math.round(image.height * scale)
  );
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Unable to process image');
  }

  context.drawImage(image, 0, 0, width, height);
  const dataUrl = canvas.toDataURL('image/jpeg', RECEIPT_JPEG_QUALITY);

  return {
    name: renameAsJpeg(file.name),
    data: getBase64Data(dataUrl),
    contentType: 'image/jpeg'
  };
};

export function CreateExpenseClaimDialog({
  dialogOpen,
  initialIban,
  initialLineItems,
  initialReceiptFiles,
  defaultKmRate = 0.23,
  title,
  onClose,
  onSubmit,
  onSaveDraft
}: Props) {
  const { t } = useTranslation();
  const rowIdRef = useRef(0);
  const nextRowId = useCallback(() => {
    rowIdRef.current += 1;
    return rowIdRef.current;
  }, []);
  const createLineItem = useCallback(
    (): DraftLineItem => ({
      rowId: nextRowId(),
      lineItemType: 'expense',
      ...emptyLineItem()
    }),
    [nextRowId]
  );
  const createTravelItem = useCallback(
    (): DraftLineItem => ({
      rowId: nextRowId(),
      lineItemType: 'travel',
      ...emptyTravelItem(defaultKmRate)
    }),
    [nextRowId, defaultKmRate]
  );
  const createDraftLineItems = useCallback(
    (sourceItems?: LineItem[]): DraftLineItem[] => {
      if (!sourceItems?.length) {
        return [createLineItem()];
      }

      return sourceItems.map(item => ({
        ...item,
        rowId: nextRowId(),
        lineItemType:
          item.kmTraveled !== undefined || item.kmRate !== undefined
            ? 'travel'
            : 'expense'
      }));
    },
    [createLineItem, nextRowId]
  );
  const createDraftReceiptFiles = useCallback(
    (sourceFiles?: InitialReceiptFile[]): DraftReceiptFile[] =>
      (sourceFiles ?? []).map((file) => ({
        rowId: nextRowId(),
        key: file.key,
        name: file.name
      })),
    [nextRowId]
  );
  const [iban, setIban] = useState(initialIban ?? '');
  const [lineItems, setLineItems] = useState<DraftLineItem[]>(
    createDraftLineItems(initialLineItems)
  );
  const [receiptFiles, setReceiptFiles] = useState<DraftReceiptFile[]>(
    createDraftReceiptFiles(initialReceiptFiles)
  );
  const [receiptsProcessing, setReceiptsProcessing] = useState(false);
  const [submissionMode, setSubmissionMode] = useState<'draft' | 'submit' | null>(
    null
  );
  const [notification, setNotification] = useState<React.ReactNode>();
  const loading = submissionMode !== null;

  const showErrorNotification = useCallback(
    (message: string) =>
      setNotification(
        <Notification
          onClose={() => setNotification(undefined)}
          severity="error"
          message={message}
        />
      ),
    []
  );

  useEffect(() => {
    if (!dialogOpen) return;

    setIban(initialIban ?? '');
    setLineItems(createDraftLineItems(initialLineItems));
    setReceiptFiles(createDraftReceiptFiles(initialReceiptFiles));
  }, [
    createDraftLineItems,
    createDraftReceiptFiles,
    dialogOpen,
    initialIban,
    initialLineItems,
    initialReceiptFiles
  ]);

  const handleClose = () => {
    if (loading) return;
    setIban(initialIban ?? '');
    setLineItems(createDraftLineItems(initialLineItems));
    setReceiptFiles(createDraftReceiptFiles(initialReceiptFiles));
    onClose();
  };

  const updateLineItem = (
    index: number,
    field: keyof LineItem,
    value: string | number | undefined
  ) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addTravelItem = () =>
    setLineItems(prev => [...prev, createTravelItem()]);

  const updateTravelKm = (index: number, km: number) => {
    setLineItems(prev => {
      const updated = [...prev];
      const item = updated[index];
      const rate = item.kmRate ?? defaultKmRate;
      updated[index] = {
        ...item,
        kmTraveled: km,
        amountIncl: Math.round(km * rate * 100) / 100
      };
      return updated;
    });
  };

  const updateTravelRate = (index: number, rate: number) => {
    setLineItems(prev => {
      const updated = [...prev];
      const item = updated[index];
      const km = item.kmTraveled ?? 0;
      updated[index] = {
        ...item,
        kmRate: rate,
        amountIncl: Math.round(km * rate * 100) / 100
      };
      return updated;
    });
  };

  const addLineItem = () =>
    setLineItems(prev =>
      prev.length > 0 ? [...prev, createLineItem()] : [createLineItem()]
    );

  const removeLineItem = (index: number) =>
    setLineItems(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.length > 0 ? filtered : [createLineItem()];
    });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;
    const selectedFiles = Array.from(files);
    // Reset input so same file can be selected again
    // eslint-disable-next-line no-param-reassign
    e.target.value = '';

    let totalReceiptBytes = receiptFiles.reduce(
      (sum, file) =>
        sum + ('key' in file ? 0 : estimateDecodedSizeBytes(file.data)),
      0
    );
    const nextReceiptFiles: DraftReceiptFile[] = [];
    const errors = new Set<string>();

    setReceiptsProcessing(true);
    try {
      for (const file of selectedFiles) {
        try {
          const preparedFile = await prepareReceiptFile(file);
          const nextSize = estimateDecodedSizeBytes(preparedFile.data);
          if (nextSize <= 0) {
            throw new Error('Unable to read file data');
          }
          if (totalReceiptBytes + nextSize > MAX_RECEIPT_TOTAL_BYTES) {
            errors.add(t('expenseReceiptTooLarge'));
            continue;
          }

          totalReceiptBytes += nextSize;
          nextReceiptFiles.push({
            rowId: nextRowId(),
            ...preparedFile
          });
        } catch {
          errors.add(t('expenseReceiptProcessingFailed'));
        }
      }

      if (nextReceiptFiles.length > 0) {
        setReceiptFiles(prev => [...prev, ...nextReceiptFiles]);
      }

      if (errors.size > 0) {
        showErrorNotification(Array.from(errors).join(' '));
      }
    } finally {
      setReceiptsProcessing(false);
    }
  };

  const removeReceipt = (index: number) =>
    setReceiptFiles(prev => prev.filter((_, i) => i !== index));

  const canSubmit =
    iban.trim().length > 0 &&
    !receiptsProcessing &&
    lineItems.length > 0 &&
    lineItems.every(item => {
      if (!item.date || item.description.trim().length === 0) return false;
      if (item.lineItemType === 'travel') {
        return (item.kmTraveled ?? 0) > 0 && (item.kmRate ?? 0) > 0;
      }
      return item.amountIncl > 0;
    });

  const canSaveDraft =
    !receiptsProcessing &&
    (iban.trim().length > 0 ||
      lineItems.some(
        (item) =>
          item.description.trim().length > 0 ||
          item.amountIncl > 0 ||
          item.vat !== undefined ||
          (item.kmTraveled ?? 0) > 0 ||
          (item.kmRate ?? 0) > 0
      ) ||
      receiptFiles.length > 0);
  const shouldShowReceiptReminder =
    lineItems.filter(
      (item) =>
        item.lineItemType === 'expense' &&
        (item.description.trim().length > 0 ||
          item.amountIncl > 0 ||
          item.vat !== undefined)
    ).length > receiptFiles.length;

  const buildResult = (): CreateExpenseClaimResult => ({
    iban: iban.trim(),
    lineItems: lineItems.map(
      ({ rowId: omittedRowId, lineItemType: omittedType, ...item }) => {
        void omittedRowId;
        void omittedType;
        return item;
      }
    ),
    receiptFiles: receiptFiles.flatMap((file) => {
      if ('key' in file) {
        return [];
      }
      return [
        (({ rowId: omittedRowId, ...upload }) => {
          void omittedRowId;
          return upload;
        })(file)
      ];
    }),
    existingReceiptFileKeys: receiptFiles.flatMap((file) =>
      'key' in file ? [file.key] : []
    )
  });

  const handleSubmit = async (
    e: Pick<React.SyntheticEvent, 'preventDefault'>,
    mode: 'draft' | 'submit' = 'submit'
  ) => {
    e.preventDefault();
    if (loading) return;
    if (mode === 'submit' && !canSubmit) return;
    if (mode === 'draft' && (!onSaveDraft || !canSaveDraft)) return;
    setSubmissionMode(mode);
    try {
      const result = buildResult();
      if (mode === 'draft') {
        await onSaveDraft?.(result);
      } else {
        await onSubmit(result);
      }
      setNotification(
        <Notification
          onClose={() => setNotification(undefined)}
          severity="success"
        />
      );
      // Reset form state directly so state is cleared even while loading=true
      setIban(initialIban ?? '');
      setLineItems([createLineItem()]);
      setReceiptFiles([]);
    } catch (err) {
      let msg = t('error');
      if (Array.isArray(err)) {
        msg = err
          .map(e => {
            try {
              return JSON.parse(e.body).error;
            } catch {
              return t('error');
            }
          })
          .join('. ');
      }
      setNotification(
        <Notification
          onClose={() => setNotification(undefined)}
          severity="error"
          message={msg}
        />
      );
    } finally {
      setSubmissionMode(null);
    }
  };

  const handleSaveDraft = async (e: React.MouseEvent<HTMLButtonElement>) => {
    await handleSubmit(e, 'draft');
  };

  return (
    <>
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <form noValidate onSubmit={(e) => handleSubmit(e, 'submit')}>
          <DialogTitle>
            <Capitalized variant="h5">{title ?? t('newExpenseClaim')}</Capitalized>
          </DialogTitle>
          <Line />
          <DialogContent>
            <Gap />
            <TextField
              label={t('iban')}
              placeholder={t('ibanPlaceholder')}
              value={iban}
              onChange={e => setIban(e.target.value)}
              fullWidth
              required
              margin="dense"
              disabled={loading}
            />
            <Gap />
            <Typography variant="subtitle2">{t('lineItems')}</Typography>
            {lineItems.map((item, idx) =>
              item.lineItemType === 'travel' ? (
                <LineItemRow
                  key={`line-item-${item.rowId}`}
                  data-testid="expense-line-item-row"
                >
                  <TextField
                    label={t('date')}
                    type="date"
                    value={item.date}
                    onChange={e => updateLineItem(idx, 'date', e.target.value)}
                    required
                    disabled={loading}
                    size="small"
                    style={{ width: 150 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label={t('description')}
                    value={item.description}
                    onChange={e =>
                      updateLineItem(idx, 'description', e.target.value)
                    }
                    required
                    disabled={loading}
                    size="small"
                    style={{ flex: 1, minWidth: 120 }}
                  />
                  <TextField
                    label={t('kmTraveled')}
                    type="number"
                    value={item.kmTraveled ?? ''}
                    onChange={e =>
                      updateTravelKm(idx, parseFloat(e.target.value) || 0)
                    }
                    required
                    disabled={loading}
                    size="small"
                    style={{ width: 80 }}
                    inputProps={{ min: 0.1, step: 0.1 }}
                  />
                  <TextField
                    label={t('kmRate')}
                    type="number"
                    value={item.kmRate ?? ''}
                    onChange={e =>
                      updateTravelRate(idx, parseFloat(e.target.value) || 0)
                    }
                    required
                    disabled={loading}
                    size="small"
                    style={{ width: 90 }}
                    inputProps={{ min: 0.001, step: 0.001 }}
                  />
                  <TextField
                    label={t('kmTotal')}
                    type="number"
                    value={item.amountIncl.toFixed(2)}
                    disabled
                    size="small"
                    style={{ width: 90 }}
                    inputProps={{ readOnly: true }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeLineItem(idx)}
                    disabled={lineItems.length === 1 || loading}
                    aria-label={t('delete')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </LineItemRow>
              ) : (
                <LineItemRow
                  key={`line-item-${item.rowId}`}
                  data-testid="expense-line-item-row"
                >
                  <TextField
                    label={t('date')}
                    type="date"
                    value={item.date}
                    onChange={e => updateLineItem(idx, 'date', e.target.value)}
                    required
                    disabled={loading}
                    size="small"
                    style={{ width: 150 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label={t('description')}
                    value={item.description}
                    onChange={e =>
                      updateLineItem(idx, 'description', e.target.value)
                    }
                    required
                    disabled={loading}
                    size="small"
                    style={{ flex: 1, minWidth: 120 }}
                  />
                  <TextField
                    label={t('vat')}
                    type="number"
                    value={item.vat ?? ''}
                    onChange={e =>
                      updateLineItem(
                        idx,
                        'vat',
                        e.target.value !== ''
                          ? parseFloat(e.target.value)
                          : undefined
                      )
                    }
                    disabled={loading}
                    size="small"
                    style={{ width: 80 }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  <TextField
                    label={t('amountIncl')}
                    type="number"
                    value={item.amountIncl || ''}
                    onChange={e =>
                      updateLineItem(
                        idx,
                        'amountIncl',
                        parseFloat(e.target.value) || 0
                      )
                    }
                    required
                    disabled={loading}
                    size="small"
                    style={{ width: 100 }}
                    inputProps={{ min: 0.01, step: 0.01 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeLineItem(idx)}
                    disabled={lineItems.length === 1 || loading}
                    aria-label={t('delete')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </LineItemRow>
              )
            )}
            <Box style={{ display: 'flex', gap: 8 }}>
              <Button
                type="button"
                size="small"
                startIcon={<AddIcon />}
                onClick={e => {
                  e.preventDefault();
                  addLineItem();
                }}
                disabled={loading}
                aria-label={t('addLineItem')}
              >
                {t('addLineItem')}
              </Button>
              <Button
                type="button"
                size="small"
                startIcon={<AddIcon />}
                onClick={e => {
                  e.preventDefault();
                  addTravelItem();
                }}
                disabled={loading}
                aria-label={t('addTravelLine')}
              >
                {t('addTravelLine')}
              </Button>
            </Box>
            <Gap />
            <Typography variant="subtitle2">{t('receipts')}</Typography>
            {shouldShowReceiptReminder ? (
              <>
                <Gap />
                <Alert severity="warning">{t('expenseReceiptReminder')}</Alert>
                <Gap />
              </>
            ) : null}
            {receiptFiles.map((f, idx) => (
              <ReceiptRow key={`receipt-${f.rowId}`}>
                <Typography variant="body2">{f.name}</Typography>
                <IconButton
                  size="small"
                  onClick={() => removeReceipt(idx)}
                  disabled={loading}
                  aria-label={t('delete')}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ReceiptRow>
            ))}
            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={loading || receiptsProcessing}
            >
              {t('uploadReceipt')}
              <input
                type="file"
                hidden
                accept="image/*,application/pdf"
                multiple
                onChange={handleFileChange}
              />
            </Button>
            <Gap />
          </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} color="primary" disabled={loading}>
                <Capitalized>{t('cancel')}</Capitalized>
              </Button>
              {onSaveDraft ? (
                <Button
                  color="primary"
                  type="button"
                  disabled={loading || !canSaveDraft}
                  onClick={handleSaveDraft}
                >
                  <Capitalized>
                    {submissionMode === 'draft' ? (
                      <CircularProgress size={24} />
                    ) : (
                      t('saveDraft')
                    )}
                  </Capitalized>
                </Button>
              ) : null}
              <Button
                color="primary"
                type="submit"
                disabled={loading || receiptsProcessing || !canSubmit}
              >
                <Capitalized>
                  {submissionMode === 'submit' || receiptsProcessing ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('submitExpenseClaim')
                  )}
                </Capitalized>
              </Button>
          </DialogActions>
        </form>
      </Dialog>
      {notification}
    </>
  );
}
